import React, { useState, useEffect } from 'react';
import { useNavigate, getRouteApi } from '@tanstack/react-router';
import { CheckCircle, Eye, EyeOff, Loader2, Mail, Phone, Lock, User, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { API_BASE_URL, SUBSIDIARY_CODE } from '../../config';
import { validatePassword, getPasswordHelperText } from '../../utils/passwordValidation';
import { authService } from '../../services/authService';
import { getBrandAsset } from '@/themes';
import { PresetBadge } from '@/components/admin/staff/shared';
import type { InvitationDetailsResponse } from '@signalvote/shared';

const routeApi = getRouteApi('/complete-registration');

const CompleteRegistration: React.FC = () => {
  const searchParams = routeApi.useSearch();
  const navigate = useNavigate();
  const token = searchParams.token;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitationDetails, setInvitationDetails] = useState<InvitationDetailsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    password: '',
    confirmPassword: ''
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing invitation token');
      setLoading(false);
      return;
    }

    fetchInvitationDetails();
  }, [token]);

  const fetchInvitationDetails = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/registration/invitation/${token}`, {
        headers: {
          'X-Subsidiary': SUBSIDIARY_CODE,
        },
      });

      if (response.ok) {
        const data: InvitationDetailsResponse = await response.json();
        setInvitationDetails(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Invalid invitation token');
      }
    } catch {
      setError('Failed to load invitation details');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.phoneNumber.trim()) {
      errors.phoneNumber = 'Phone number is required';
    } else if (!/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.phoneNumber)) {
      errors.phoneNumber = 'Please enter a valid phone number';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else {
      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid) {
        errors.password = passwordValidation.errors[0];
      }
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/registration/complete-invitation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Subsidiary': SUBSIDIARY_CODE,
        },
        body: JSON.stringify({
          token,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          phoneNumber: formData.phoneNumber.trim(),
          password: formData.password
        })
      });

      if (response.ok) {
        setSuccess(true);
        authService.logout();
        setTimeout(() => {
          navigate({ to: '/login' });
        }, 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to complete registration');
      }
    } catch {
      setError('Failed to complete registration. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error && !invitationDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <img
              src={getBrandAsset('logo.svg')}
              alt="Logo"
              className="h-12 w-auto mx-auto mb-4"
            />
            <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Invalid Invitation</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button variant="outline" onClick={() => navigate({ to: '/login' })}>
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <img
              src={getBrandAsset('logo.svg')}
              alt="Logo"
              className="h-12 w-auto mx-auto mb-4"
            />
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Registration Complete!</h2>
            <p className="text-muted-foreground mb-4">
              Your account has been created successfully. Redirecting to login...
            </p>
            <Loader2 className="h-5 w-5 animate-spin text-primary mx-auto" />
          </CardContent>
        </Card>
      </div>
    );
  }

  const campaignName = invitationDetails?.campaignName;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-muted/30">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center space-y-2">
          <img
            src={getBrandAsset('logo.svg')}
            alt="Logo"
            className="h-12 w-auto mx-auto"
          />
          <CardTitle className="text-2xl">Complete Your Registration</CardTitle>
          {campaignName && (
            <CardDescription className="text-base">
              Join the {campaignName} team
            </CardDescription>
          )}
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Stepper */}
          <div className="flex items-center gap-2">
            <StepIndicator step={1} label="Invited" completed />
            <div className="flex-1 h-px bg-border" />
            <StepIndicator step={2} label="Details" active />
            <div className="flex-1 h-px bg-border" />
            <StepIndicator step={3} label="Done" />
          </div>

          {/* Invitation Info */}
          <div className="rounded-lg border bg-muted/50 p-4 space-y-1">
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Email:</span>
              <span className="font-medium">{invitationDetails?.email}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Role:</span>
              {invitationDetails && <PresetBadge isOwner={false} presetKey={invitationDetails.presetKey} />}
            </div>
          </div>

          {error && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          <Separator />

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name fields */}
            <div className="grid grid-cols-2 gap-3">
              <FormField
                label="First Name"
                error={validationErrors.firstName}
              >
                <Input
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  disabled={submitting}
                  autoFocus
                  className={cn(validationErrors.firstName && 'border-destructive')}
                />
              </FormField>
              <FormField
                label="Last Name"
                error={validationErrors.lastName}
              >
                <Input
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  disabled={submitting}
                  className={cn(validationErrors.lastName && 'border-destructive')}
                />
              </FormField>
            </div>

            <FormField
              label="Phone Number"
              error={validationErrors.phoneNumber}
            >
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="(555) 123-4567"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  disabled={submitting}
                  className={cn('pl-9', validationErrors.phoneNumber && 'border-destructive')}
                />
              </div>
            </FormField>

            <FormField
              label="Password"
              error={validationErrors.password}
              hint={!validationErrors.password ? getPasswordHelperText(formData.password) : undefined}
            >
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  disabled={submitting}
                  className={cn('pl-9 pr-10', validationErrors.password && 'border-destructive')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </FormField>

            <FormField
              label="Confirm Password"
              error={validationErrors.confirmPassword}
            >
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  disabled={submitting}
                  className={cn('pl-9 pr-10', validationErrors.confirmPassword && 'border-destructive')}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </FormField>

            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating Account...
                </>
              ) : (
                'Complete Registration'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

function StepIndicator({ step, label, completed, active }: { step: number; label: string; completed?: boolean; active?: boolean }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div className={cn(
        'h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium',
        completed && 'bg-green-500 text-white',
        active && 'bg-primary text-primary-foreground',
        !completed && !active && 'bg-muted text-muted-foreground'
      )}>
        {completed ? <CheckCircle className="h-4 w-4" /> : step}
      </div>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

function FormField({ label, error, hint, children }: { label: string; error?: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium">{label}</label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
      {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export default CompleteRegistration;
